import React, { useContext, useEffect, useRef } from 'react';
import {
  ControlContainer,
  FullscreenButton,
  Image,
  PlayButton,
  styled,
  Text,
  TextAlignment,
  TextTag,
  TimeDisplay,
  Timeline,
  TokenTextAppearance,
  TokenTextColor,
  Video,
} from '@volkswagen-onehub/components-core';
import { useTranslation } from 'react-i18next';
import { GiveawayResponse, TaskStatus } from '@org-avp/avp-hck-dga-customerui-commons';
import { GiveawayContext } from '@/state/state';
import { minimalScreenWidth, size } from '@/common/breakpoints';
import { VideoTracker } from './videoTracker';
import { useInView } from '@/hooks/useInView';
import useBreakpoint from '@/hooks/useBreakpoint';

const StyledTextContainer = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  margin: auto;
  padding-bottom: ${(props) => props.theme.size.dynamic0100};
  padding-left: ${(props) => props.theme.size.dynamic0200};
  padding-right: ${(props) => props.theme.size.dynamic0200};
  color: white;

  @media screen and ${minimalScreenWidth.m} {
    max-width: ${size.m};
    padding-left: ${(props) => props.theme.size.dynamic0200};
    padding-right: ${(props) => props.theme.size.dynamic0200};
    padding-bottom: ${(props) => props.theme.size.dynamic0200};
  }

  @media screen and ${minimalScreenWidth.l} {
    max-width: ${size.m};
    padding-left: 0;
    padding-right: 0;
    padding-bottom: ${(props) => props.theme.size.dynamic0300};
  }

  @media screen and ${minimalScreenWidth.xl} {
    max-width: ${size.l};
    padding-bottom: ${(props) => props.theme.size.dynamic0300};
  }

  @media screen and ${minimalScreenWidth.xxl} {
    max-width: ${size.xl};
    padding-bottom: ${(props) => props.theme.size.dynamic0300};
  }

  @media screen and ${minimalScreenWidth.xxxl} {
    max-width: ${size.xxl};
    padding-bottom: ${(props) => props.theme.size.dynamic0300};
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

export const CameraFlight = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const giveawayData: GiveawayResponse = useContext(GiveawayContext);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { rootMargin: '50px' });

  useEffect(() => {
    const videoElement = ref.current?.querySelector('video');
    if (!videoElement) return;

    if (inView) {
      const promise = videoElement.play();
      if (promise) {
        promise.then().catch(() => {});
      }
    } else {
      videoElement.pause();
    }
  }, [inView]);

  const processingCompleted = [
    TaskStatus.FINISHED,
    TaskStatus.EXPIRED,
    TaskStatus.ABORTED,
  ].includes(giveawayData.giveawayStatus);

  if (giveawayData.renderCameraFlight && processingCompleted) {
    return giveawayData.cameraFlightVideo ? (
      <StyledContainer ref={ref}>
        {/* Muted prop is needed to autoplay the video */}
        <Video
          src={giveawayData.cameraFlightVideo}
          data-testid="cameraflight-video"
          tabIndex={0}
          loop
          autoPlay
          muted
          /**
           * note: playsInline is needed for iOS to play the video automatically
           * and it only works when the video is muted
           */
          playsInline
          poster="https://volkswagen.dga.avp.tech/assets/camera_flight_placeholder_v.png"
        >
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore  */}
          <ControlContainer>
            <PlayButton
              playTitle={t(`videoControls.playTitle`)}
              pauseTitle={t(`videoControls.pauseTitle`)}
            />
            <Timeline />
            <TimeDisplay />
            <FullscreenButton
              openTitle={t(`videoControls.openTitle`)}
              closeTitle={t(`videoControls.closeTitle`)}
            />
          </ControlContainer>
          <VideoTracker />
        </Video>
      </StyledContainer>
    ) : (
      <StyledContainer>
        <Image
          src="https://volkswagen.dga.avp.tech/assets/camera_flight_placeholder_v.png"
          alt="Camera Flight Placeholder"
          data-testid="cameraflight-placeholder"
          aria-labelledby="cameraflight-placeholder-text"
        />
        <StyledTextContainer>
          <Text
            tag={TextTag.p}
            appearance={
              isMobile ? TokenTextAppearance.headline300 : TokenTextAppearance.headline100
            }
            textAlign={TextAlignment.center}
            color={TokenTextColor.inherit}
            data-testid="cameraflight-placeholder-text"
            id="cameraflight-placeholder-text"
          >
            {t('giveaway.cameraFlightPlaceholder')}
          </Text>
        </StyledTextContainer>
      </StyledContainer>
    );
  }

  return <></>;
};
